import SText from '../../../components/opinionless/SText'
import SButton from '../../../components/opinionless/SButton'
import React, { useEffect, useMemo, useState } from 'react'
import SFlex from 'components/opinionless/SFlex'
import SLink from 'components/opinionless/SLink'
import STheme, { SStyled, useSTheme } from '../../../styles/theme'
import { GoogleReCaptchaProvider, GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from '../../../i18n'
import { useRouter } from 'next/router'

import { useGetProgramsMutation } from 'types/graphql'
import { Flex } from 'rebass'
import ConditionalRender from 'utils/conditionalRender'
import Logos from '../../Enrollment/SignUpWizard/Logos'
import SBox from '../../../components/opinionless/SBox'
import { useOrganization } from '../../../v2/context/organization'
import { ga4_useTitle } from 'utils/ga4'

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

interface Props {
    setCurrentStep?: (number) => void
    fontFamily: any
}

const ApplicationStep0: React.FC<Props> = (props: Props) => {
    const theme = useSTheme()
    const [isButtonDisabled, setIsButtonDislabed] = useState(true)
    const { t } = useTranslation(window.LANG_NS)
    const router = useRouter()
    const [getProgram] = useGetProgramsMutation()
    const { program } = useOrganization()
    const [isExistingProgram, setIsExistingProgram] = useState(false)

    const fontFamily = props.fontFamily

    ga4_useTitle('Step 0')

    const isSonoma = useMemo(() => {
        return (
            window.PROGRAM.name.toLowerCase().includes('sonoma') ||
            window.PROGRAM.name.toLowerCase().includes('pathway')
        )
    }, [window])

    const isChicagoFTX = useMemo(() => {
        return (
            window.PROGRAM.name.toLowerCase().includes('chicago') && window.PROGRAM.name.toLowerCase().includes('ftx')
        )
    }, [window])

    const isLongBeach = useMemo(() => {
        return window.PROGRAM.name.toLowerCase().includes('long') && window.PROGRAM.name.toLowerCase().includes('beach')
    }, [window])
    useEffect(() => {
        getProgram({
            variables: {
                programUrl: router.query['program-name'] as string,
                includeClosedPeriod: false,
            },
        }).then((res) => {
            if (res.data.getPrograms?.success) {
                setIsExistingProgram(true)
            } else {
                if (isSonoma) {
                    return router.push(
                        `${window.PERSON?.applicationDetails[0].program?.urlFragment || program.urlFragment}/login`
                    )
                }
                router.push(`${window.PERSON?.applicationDetails[0].program?.urlFragment || program?.urlFragment}/404`)
            }
        })
    }, [])

    const PressRow = SStyled(SFlex)`
    margin: 20px 0 20px;
    width: 100%;
    align-items: center;
    justify-content: center;
`

    const PressLink = SStyled(SLink)`
    margin-right: ${(p) => p.theme.space[5]}px;
`

    if (isExistingProgram) {
        return (
            <Container>
                <SText
                    textAlign={'left'}
                    fontWeight={'700'}
                    fontSize={5}
                    mb={8}
                    color={STheme.colors.textColor}
                    lineHeight={1}
                    fontFamily={fontFamily.header}
                >
                    {t('application_header')}
                </SText>
                <ConditionalRender if={t('application_sub_header').length > 0}>
                    <SText
                        fontSize={4}
                        textAlign={'left'}
                        fontWeight={'700'}
                        lineHeight={'22px'}
                        color={STheme.colors.textColor}
                        mb={6}
                        sx={{ whiteSpace: 'pre-line' }}
                        fontFamily={fontFamily.body}
                    >
                        {t('application_sub_header')}
                    </SText>
                </ConditionalRender>

                <SText
                    fontSize={3}
                    fontWeight={'regular'}
                    color={STheme.colors.textColor}
                    mb={5}
                    sx={{ whiteSpace: 'pre-line', lineHeight: '18px' }}
                    fontFamily={fontFamily.body}
                    dangerouslySetInnerHTML={{ __html: t('application_body') }}
                ></SText>
                <ConditionalRender if={isSonoma}>
                    <Logos />
                </ConditionalRender>
                <ConditionalRender if={isChicagoFTX}>
                    <Logos />
                </ConditionalRender>
                <ConditionalRender if={!isSonoma && !isChicagoFTX && !isLongBeach}>
                    <PressRow>
                        <PressLink target="__blank" href={'https://f4gi.org/'}>
                            <Flex mt={5} mb={5}>
                                <img
                                    src={
                                        'https://storage.googleapis.com/portal-web-resources/Pretrial/F4GI_Horizontal_Black.png'
                                    }
                                    style={{
                                        width: 'auto',
                                        height: '100%',
                                    }}
                                />
                            </Flex>
                        </PressLink>
                    </PressRow>
                </ConditionalRender>

                <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}>
                    <GoogleReCaptcha
                        onVerify={() => {
                            setIsButtonDislabed(false)
                        }}
                    />
                </GoogleReCaptchaProvider>

                <SButton
                    onClick={() => props.setCurrentStep(1)}
                    title="Next"
                    sx={{ [isButtonDisabled ? 'bg' : '']: 'gray200' }}
                    disabled={isButtonDisabled}
                    mb={5}
                    width={'100%'}
                    textStyle={{ fontFamily: fontFamily.body }}
                    ga4_key={t('continue', { lang: 'en' })}
                >
                    {t('continue')}
                </SButton>
                <ConditionalRender if={window.PROGRAM.name === 'Pathway to Income Equity'}>
                    <SLink
                        href="https://first5sonomacounty.org/wp-content/uploads/2021/11/FAQS-GBI-1.pdf"
                        target="_blank"
                        style={{ color: theme.colors.tertiaryColor, marginTop: '20px', fontFamily: fontFamily.body }}
                    >
                        <span>{t('need_help_question')}</span>
                    </SLink>
                </ConditionalRender>
                <ConditionalRender
                    if={window.PROGRAM.name !== 'Pathway to Income Equity' && !isChicagoFTX && !isLongBeach}
                >
                    <SLink
                        href={`${
                            window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                        }/help`}
                        style={{ color: theme.colors.tertiaryColor, marginTop: '20px', fontFamily: fontFamily.body }}
                    >
                        <span>{t('need_help_question')}</span>
                    </SLink>
                </ConditionalRender>
                <ConditionalRender if={isLongBeach}>
                    <SLink
                        href="http://longbeachpledge.org"
                        style={{
                            color: theme.colors.primaryColor,
                            fontWeight: 700,
                            marginTop: '20px',
                            fontSize: '14px',
                            fontFamily: fontFamily.body,
                            lineHeight: '18.2px',
                        }}
                        target="_blank"
                    >
                        <span>{t('need_help_question')}</span>
                    </SLink>
                </ConditionalRender>
            </Container>
        )
    } else {
        return null
    }
}

export default ApplicationStep0
